import React, { useState, useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────
   MODAL VARIANTS
   type: 'add' | 'edit' | 'delete'
───────────────────────────────────────────── */
export default function Modal({
  type,
  room, // Now receives the full room object { id, name }
  onConfirmAdd,
  onConfirmEdit,
  onConfirmDelete,
  onClose,
}) {
  const [inputValue, setInputValue] = useState(type === 'edit' && room ? room.name : '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (type === 'add' || type === 'edit') {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [type, onClose]);

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) onConfirmAdd(inputValue);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) onConfirmEdit(room, inputValue);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-[#242424] p-7 flex flex-col gap-6"
        style={{ backgroundColor: '#0e0e0e' }}
      >

        {type === 'add' && (
          <>
            <ModalHeader
              title="Add Room"
              subtitle="Add a new room to your dashboard. This will generate a unique display link for that room's tablet."
              onClose={onClose}
            />
            <form onSubmit={handleAddSubmit} className="flex flex-col gap-4">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Room name..."
                maxLength={32}
                className="w-full bg-[#161616] border border-[#2a2a2a] rounded-xl px-4 py-3.5 text-sm text-white placeholder-[#3a3a3a] focus:outline-none focus:border-[#404040] transition-colors"
              />
              <ModalActions
                onCancel={onClose}
                primaryLabel="Create Room"
                primaryDisabled={!inputValue.trim()}
                primaryVariant="white"
              />
            </form>
          </>
        )}

        {type === 'edit' && (
          <>
            <ModalHeader
              title="Edit Room Name"
              subtitle="Rename this room across the dashboard and the active display."
              onClose={onClose}
            />
            <form onSubmit={handleEditSubmit} className="flex flex-col gap-4">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                maxLength={32}
                className="w-full bg-[#161616] border border-[#2a2a2a] rounded-xl px-4 py-3.5 text-sm text-white placeholder-[#3a3a3a] focus:outline-none focus:border-[#404040] transition-colors"
              />
              <ModalActions
                onCancel={onClose}
                primaryLabel="Save Changes"
                primaryDisabled={!inputValue.trim() || inputValue.trim() === room?.name}
                primaryVariant="white"
              />
            </form>
          </>
        )}

        {type === 'delete' && (
          <>
            <ModalHeader
              title="Delete Room"
              subtitle="This will permanently remove this room from the dashboard. Any active display links for this room will immediately break."
              onClose={onClose}
            />
            <div className="flex gap-2.5">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-muted border border-[#222222] hover:text-white hover:border-[#383838] transition-all duration-150"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirmDelete(room)}
                className="flex-1 py-3 rounded-xl text-sm font-bold border transition-all duration-200"
                style={{
                  backgroundColor: 'rgba(160,35,35,0.12)',
                  borderColor:     'rgba(160,35,35,0.32)',
                  color:           '#d95555',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(160,35,35,0.22)';
                  e.currentTarget.style.borderColor     = 'rgba(160,35,35,0.55)';
                  e.currentTarget.style.color           = '#f06060';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(160,35,35,0.12)';
                  e.currentTarget.style.borderColor     = 'rgba(160,35,35,0.32)';
                  e.currentTarget.style.color           = '#d95555';
                }}
              >
                Delete Room
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-sm font-bold text-white tracking-wide">{title}</h2>
        <p className="text-xs leading-relaxed" style={{ color: '#777777', maxWidth: '32ch' }}>
          {subtitle}
        </p>
      </div>
      <button
        onClick={onClose}
        aria-label="Close"
        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-muted hover:text-white hover:bg-[#1e1e1e] transition-all duration-150 mt-0.5"
      >
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path d="M1 1l9 9M10 1L1 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

function ModalActions({ onCancel, primaryLabel, primaryDisabled, primaryVariant }) {
  return (
    <div className="flex gap-2.5">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 py-3 rounded-xl text-sm font-semibold text-muted border border-[#222222] hover:text-white hover:border-[#383838] transition-all duration-150"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={primaryDisabled}
        className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-150 disabled:opacity-25 disabled:cursor-not-allowed ${
          primaryVariant === 'white'
            ? 'bg-white text-black hover:bg-[#e8e8e8]'
            : 'bg-[#c93333] text-white hover:bg-[#b02b2b]'
        }`}
      >
        {primaryLabel}
      </button>
    </div>
  );
}